import { useEffect, useState, useCallback } from "react";
import { Copy, Check, Link2, Ban, RotateCcw, Plus, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatApiError } from "../../lib/AuthContext";

const STATUS_STYLE = {
  actief: { color: "#4ADE80", label: "ACTIEF" },
  inactief: { color: "#A3A3A3", label: "INACTIEF" },
  verlopen: { color: "#F87171", label: "VERLOPEN" },
};

/* Admin → PT → Booking Links: persoonlijke boekingspagina's voor specifieke leden. */
export default function AdminPTLinks() {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  const load = useCallback(() => {
    api.get("/pt/admin/links").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const linkUrl = (token) => `${window.location.origin}/pt/boek/${token}`;

  const waUrl = (l) => `https://wa.me/?text=${encodeURIComponent(
    `Hoi ${l.name.split(" ")[0]}! Via deze persoonlijke link boek je direct je Personal Training bij JOH Boxing — kies zelf een moment dat je past: ${linkUrl(l.token)}`
  )}`;

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/pt/admin/links", { name: name.trim(), expires_at: expires || null });
      toast.success("Boekingslink aangemaakt");
      setName(""); setExpires("");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Aanmaken mislukt"); }
    setBusy(false);
  };

  const copy = async (l) => {
    try {
      await navigator.clipboard.writeText(linkUrl(l.token));
      setCopied(l.id);
      setTimeout(() => setCopied(""), 1500);
    } catch { toast.error("Kopiëren mislukt"); }
  };

  const toggle = async (l) => {
    try {
      await api.post(`/pt/admin/links/${l.id}/${l.active ? "deactivate" : "activate"}`);
      toast.success(l.active ? "Link gedeactiveerd" : "Link weer actief");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Mislukt"); }
  };

  return (
    <div className="max-w-3xl" data-testid="pt-links">
      <div className="border border-[#F5F3F0]/15 p-5 md:p-6 mb-8">
        <div className="chip text-[#A3A3A3] mb-4">— Nieuwe boekingslink</div>
        <form onSubmit={create} className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label className="chip text-[#A3A3A3]">Naam van het lid</label>
            <input value={name} onChange={(e) => setName(e.target.value)} className="editorial-input dark w-full" placeholder="Bv. Sam de Vries" data-testid="pt-link-name" />
          </div>
          <div>
            <label className="chip text-[#A3A3A3]">Vervaldatum (optioneel)</label>
            <input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} className="editorial-input dark w-full" data-testid="pt-link-expires" />
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={busy || !name.trim()} className="btn-light justify-center w-full disabled:opacity-40" data-testid="pt-link-create">
              <Plus size={14} /> {busy ? "Aanmaken…" : "Maak link"}
            </button>
          </div>
        </form>
        <div className="chip text-[#4A4A4A] mt-3">Het lid opent de link, kiest een moment en vult naam, e-mail en telefoon in — de boeking verschijnt hier als aanvraag.</div>
      </div>

      {items === null ? <div className="chip text-[#A3A3A3]">Laden…</div> : items.length === 0 ? (
        <div className="border border-[#F5F3F0]/15 p-10 text-center chip text-[#A3A3A3]" data-testid="pt-links-empty">Nog geen boekingslinks aangemaakt.</div>
      ) : (
        <div className="line-t line-dark" data-testid="pt-links-list">
          {items.map((l) => {
            const st = STATUS_STYLE[l.status] || STATUS_STYLE.actief;
            return (
              <div key={l.id} className="line-b line-dark py-4" data-testid={`pt-link-row-${l.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display uppercase text-xl flex-1 min-w-32 truncate">{l.name}</span>
                  <span className="chip px-2 py-0.5 border" style={{ color: st.color, borderColor: "currentColor" }} data-testid={`pt-link-status-${l.id}`}>{st.label}</span>
                  <button onClick={() => copy(l)} className="chip text-[#A3A3A3] hover:text-[#F5F3F0] inline-flex items-center gap-1.5 transition-colors" data-testid={`pt-link-copy-${l.id}`}>
                    {copied === l.id ? <Check size={13} /> : <Copy size={13} />} Kopieer link
                  </button>
                  {l.status === "actief" && (
                    <a href={waUrl(l)} target="_blank" rel="noreferrer" className="chip text-[#4ADE80] hover:text-[#4ADE80]/80 inline-flex items-center gap-1.5 transition-colors" data-testid={`pt-link-whatsapp-${l.id}`}>
                      <MessageCircle size={13} /> WhatsApp
                    </a>
                  )}
                  {l.status !== "verlopen" && (
                    <button onClick={() => toggle(l)} className={`chip inline-flex items-center gap-1.5 transition-colors ${l.active ? "text-red-400 hover:text-red-300" : "text-[#4ADE80] hover:text-[#4ADE80]/80"}`} data-testid={`pt-link-toggle-${l.id}`}>
                      {l.active ? <><Ban size={13} /> Deactiveer</> : <><RotateCcw size={13} /> Activeer</>}
                    </button>
                  )}
                </div>
                <div className="chip text-[#4A4A4A] mt-2 break-all">
                  {l.expires_at ? `verloopt ${l.expires_at} · ` : "geen vervaldatum · "}
                  {l.used_count || 0}× gebruikt · aangemaakt {l.created_at ? new Date(l.created_at).toLocaleDateString("nl-NL") : "—"}
                </div>
                <div className="chip text-[#4A4A4A] mt-1 break-all flex items-center gap-1.5"><Link2 size={11} />{linkUrl(l.token)}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
